import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

const ProjectsTable = ({ 
  projects, 
  selectedProjectId, 
  onSelectProject, 
  onDelete,
  emptyStateConfig = {}
}) => {
  const navigate = useNavigate();
  
  const {
    title = 'No Prompts Available',
    description = 'Create and complete your first prompt to get started.',
    icon: EmptyIcon,
    actionLabel = 'Go to Prompts',
    onAction = () => navigate('/prompts')
  } = emptyStateConfig;

  const handleDelete = async (e, project) => {
    e.stopPropagation();
    
    if (onDelete) {
      onDelete(e, project);
    }
  };

  // Format date to a readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Get icon color based on output type
  const getFormatColor = (format) => {
    switch (format?.toLowerCase()) {
      case 'plain text': return 'text-green-400';
      case 'markdown': return 'text-purple-400';
      case 'schema': return 'text-cyan-400';
      case 'xml': return 'text-orange-400';
      case 'csv': return 'text-amber-400';
      default: return 'text-primary-400';
    }
  };

  if (projects.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-20 px-6">
        <div className="doc-empty-state">
          <div className="doc-empty-icon">
          {EmptyIcon ? (
              <EmptyIcon className="w-12 h-12 text-primary-500/60" />
          ) : (
              <svg className="w-12 h-12 text-primary-500/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          )}
        </div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">{title}</h3>
          <p className="text-sm text-text-secondary mb-6 max-w-sm text-center leading-relaxed">{description}</p>
          <button onClick={onAction} className="doc-empty-action">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14m-7-7h14"/>
          </svg>
            <span>{actionLabel}</span>
        </button>
        </div>
      </div>
    );
  }

  return (
    <div className="doc-list-container">
      {/* Document List */}
      <div className="doc-list">
        {projects.map((project, index) => (
          <div
                key={project.id}
            className={`doc-item ${selectedProjectId === project.id ? 'doc-item-selected' : ''}`}
                onClick={() => onSelectProject(project)}
            style={{ animationDelay: `${index * 30}ms` }}
          >
            {/* Document Icon */}
            <div className="doc-icon-wrapper">
              <div className={`doc-icon ${getFormatColor(project.user_input?.output_type)}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  <path d="M13 3v5a1 1 0 001 1h5"/>
                    </svg>
              </div>
                  </div>

            {/* Document Content */}
            <div className="doc-content">
              <div className="doc-header">
                <h3 className="doc-title">{project.project_name || 'Untitled'}</h3>
                <div className="doc-badges">
                  <span className="doc-badge doc-badge-type">
                    {project.user_input?.type || 'Prompt'}
                  </span>
                  <span className={`doc-badge doc-badge-format ${getFormatColor(project.user_input?.output_type)}`}>
                    {project.user_input?.output_type || 'Text'}
                  </span>
                </div>
              </div>
              
              <p className="doc-description">
                {project.user_input?.description || 'No description provided'}
              </p>
              
              <div className="doc-meta">
                <div className="doc-meta-item">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <span>{formatDate(project.created_at)}</span>
                </div>
                <div className="doc-meta-item">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span className={`doc-permission ${
                    project.permission === 'owner' ? 'text-amber-400' : 
                    project.permission === 'edit' ? 'text-green-400' : 'text-blue-400'
                  }`}>
                    {project.permission ? project.permission.charAt(0).toUpperCase() + project.permission.slice(1) : 'Owner'}
                  </span>
                </div>
              </div>
      </div>

            {/* Document Actions */}
            <div className="doc-actions">
              <button
                className="doc-action-btn doc-action-open"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectProject(project);
                }}
                title="Open document"
                aria-label="Open document"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </button>
              
              {onDelete && project.permission === 'owner' && (
                <button
                  className="doc-action-btn doc-action-delete"
                  onClick={(e) => handleDelete(e, project)}
                  title="Delete document"
                  aria-label="Delete document"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

ProjectsTable.propTypes = {
  projects: PropTypes.array.isRequired,
  selectedProjectId: PropTypes.string,
  onSelectProject: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  emptyStateConfig: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.elementType,
    actionLabel: PropTypes.string,
    onAction: PropTypes.func,
  }),
};

ProjectsTable.defaultProps = {
  selectedProjectId: null,
  onDelete: null,
  emptyStateConfig: {},
};

export default ProjectsTable;
